import m from 'mithril';
import { Store } from '../store';
import { t } from '../i18n';

export const ProductCard: m.Component<{ product: any }> = {
    oninit(vnode: any) {
        vnode.state.hoverIndex = 0;
        vnode.state.timer = null;
        
        // Decide style deterministically based on product id
        const styles = ['dots', 'hover-cycle', 'thumbnails'];
        vnode.state.galleryStyle = styles[(vnode.attrs.product.id || 0) % styles.length];
        
        vnode.state.startHover = () => {
            if (vnode.state.galleryStyle === 'hover-cycle') {
                const images = vnode.attrs.product.images || [];
                if (images.length > 1 && !vnode.state.timer) {
                    vnode.state.timer = setInterval(() => {
                        vnode.state.hoverIndex = (vnode.state.hoverIndex + 1) % images.length;
                        m.redraw();
                    }, 1200);
                }
            }
        };
        
        vnode.state.stopHover = () => {
            if (vnode.state.timer) {
                clearInterval(vnode.state.timer);
                vnode.state.timer = null;
                vnode.state.hoverIndex = 0; // reset to 0
                m.redraw();
            }
        };
    },
    onremove(vnode: any) {
        if (vnode.state.timer) clearInterval(vnode.state.timer);
    },
    view(vnode: any) {
        const { product: p } = vnode.attrs;
        const { hoverIndex, galleryStyle, startHover, stopHover } = vnode.state;
        const isFav = Store.state.favorites.some(f => f.id === p.id);
        const inCart = Store.state.cart.some((item: any) => (item.product?.id || item.id) === p.id);
        
        const images = p.images && p.images.length > 0 ? p.images : [`https://picsum.photos/seed/${p.id}/400/500`];
        const displayImages = images.slice(0, 5);
        const hasMore = images.length > 5;
        
        let imageSection;
        if (images.length === 1) {
            imageSection = m('div', { class: 'w-full bg-[var(--color-bg-base)] aspect-[4/5] overflow-hidden relative' }, [
                m('img', {
                    src: images[0],
                    loading: 'lazy',
                    class: 'w-full h-full object-cover transition-transform duration-700 group-hover:scale-110',
                })
            ]);
        } else if (galleryStyle === 'dots') {
            imageSection = m('div', { class: 'w-full bg-[var(--color-bg-base)] aspect-[4/5] overflow-hidden relative group/dots' }, [
                m('img', {
                    src: images[hoverIndex],
                    loading: 'lazy',
                    class: 'w-full h-full object-cover transition-all duration-500 group-hover/dots:scale-105',
                }),
                // Dots pagination
                m('div', { class: 'absolute bottom-3 inset-x-0 flex justify-center items-center gap-1.5 z-10 opacity-0 group-hover/dots:opacity-100 transition-opacity' }, [
                    displayImages.map((_: any, idx: number) => m('button', {
                        class: `w-1.5 h-1.5 rounded-full transition-all border border-black/10 ${hoverIndex === idx ? 'bg-[var(--color-primary)] scale-125 shadow-sm' : 'bg-white/80 hover:bg-white'}`,
                        onclick: (e: Event) => { e.stopPropagation(); vnode.state.hoverIndex = idx; }
                    })),
                    hasMore && m('span', { class: 'text-[9px] bg-black/50 backdrop-blur-md text-white font-bold ms-1 px-1.5 rounded flex items-center shadow-sm' }, `+${images.length - 5}`)
                ])
            ]);
        } else if (galleryStyle === 'hover-cycle') {
            imageSection = m('div', { 
                class: 'w-full bg-[var(--color-bg-base)] aspect-[4/5] overflow-hidden relative group/cycle',
                onmouseenter: startHover,
                onmouseleave: stopHover,
            }, [
                m('img', {
                    src: images[hoverIndex],
                    loading: 'lazy',
                    class: 'w-full h-full object-cover transition-opacity duration-300',
                }),
                m('div', { class: 'absolute bottom-3 start-3 bg-black/40 backdrop-blur-md border border-white/10 text-white text-[9px] font-bold px-2 py-1 rounded-md opacity-0 group-hover/cycle:opacity-100 transition-opacity' }, `${hoverIndex + 1}/${images.length}`)
            ]);
        } else if (galleryStyle === 'thumbnails') {
            imageSection = m('div', { class: 'w-full bg-[var(--color-bg-base)] aspect-[4/5] overflow-hidden flex flex-col' }, [
                m('div', { class: 'w-full flex-1 overflow-hidden relative' }, [
                    m('img', {
                        src: images[hoverIndex],
                        loading: 'lazy',
                        class: 'w-full h-full object-cover transition-transform duration-700 group-hover:scale-105',
                    })
                ]),
                m('div', { class: 'flex gap-1 p-1.5 h-12 shrink-0 bg-[var(--color-surface)] border-t border-[var(--color-border)]' }, [
                    displayImages.slice(0, 4).map((img: string, idx: number) => m('div', {
                        class: `h-full flex-1 overflow-hidden cursor-pointer rounded-[4px] transition-all border ${hoverIndex === idx ? 'border-[var(--color-primary)] shadow-[0_0_0_1px_var(--color-primary)]' : 'border-transparent opacity-60'} hover:opacity-100`,
                        onmouseenter: () => vnode.state.hoverIndex = idx
                    }, m('img', { src: img, class: 'w-full h-full object-cover mix-blend-multiply' }))),
                    images.length > 4 && m('div', { class: 'h-full flex-1 bg-black/5 rounded-[4px] flex items-center justify-center text-[10px] font-bold opacity-60' }, `+${images.length - 4}`)
                ])
            ]);
        }
        
        return m('div', {
            class: 'hover-spring group cursor-pointer bg-[var(--color-surface)] border border-[var(--color-border)] hover:border-[var(--color-primary)] rounded-2xl overflow-hidden flex flex-col relative shadow-[0_4px_15px_-5px_rgba(0,0,0,0.05)] hover:shadow-[0_15px_40px_-10px_var(--color-primary-glow)] transition-all duration-300',
            onclick: () => m.route.set(`/product/${p.id}`)
        }, [
            // Wishlist
            m('button', {
                class: `!m-0 !p-0 !border-0 !bg-transparent absolute top-3 end-3 z-20 w-8 h-8 rounded-full bg-[var(--color-bg-base)]/60 backdrop-blur-md shadow-sm border flex items-center justify-center text-sm transition-all ${
                    isFav 
                    ? 'opacity-100 text-red-500 border-red-500 bg-red-50' 
                    : 'opacity-0 group-hover:opacity-100 border-[var(--color-border)] text-[var(--color-text-base)] hover:text-red-500 hover:border-red-500'
                }`,
                onclick: (e: Event) => {
                    e.stopPropagation();
                    Store.toggleFavorite(p);
                },
            }, m('iconify-icon', { 
                icon: 'lucide:heart', 
                class: isFav ? 'animate-scale-in text-red-500 drop-shadow-md' : ''
            })),

            // Image Section
            m('div', { class: 'relative' }, [
                imageSection,
                m('span', { class: 'absolute top-3 start-3 z-10 text-[10px] font-black uppercase tracking-widest bg-gradient-to-br from-[var(--color-primary)] to-[var(--color-secondary)] text-white px-3 py-1.5 rounded-full shadow-[0_4px_10px_var(--color-primary-glow)] backdrop-blur-md border border-white/20 pointer-events-none' }, t('product.new') ?? 'NEW'),
            ]),

            // Info Container
            m('div', { class: 'p-4 sm:p-5 flex flex-col flex-1 gap-1.5' }, [
                m('div', { class: 'flex items-center justify-between mb-0.5' }, [
                    m('p', { class: 'text-[10px] sm:text-xs font-bold uppercase tracking-widest opacity-50 text-[var(--color-primary)] line-clamp-1' }, p.category ?? 'Product'),
                    m('div', { class: 'flex items-center gap-1 text-[10px] font-bold opacity-70 shrink-0' }, [
                        m('iconify-icon', { icon: 'lucide:star', class: 'text-yellow-500' }),
                        (p.rating && p.rating !== '0.0') ? p.rating : 'New'
                    ])
                ]),
                m('h3', {
                    class: 'font-black text-sm sm:text-base line-clamp-2 leading-snug group-hover:text-[var(--color-primary)] transition-colors'
                }, p.name),
                
                m('div', { class: 'mt-auto flex items-center justify-between pt-4' }, [
                    m('div', { class: 'flex flex-col' }, [
                        m('span', { class: 'text-[10px] font-semibold opacity-60 uppercase tracking-widest' }, 'Price'),
                        m('span', { class: 'text-lg sm:text-xl font-black text-[var(--color-primary)] tracking-tight' },
                            `$${Number(p.price).toFixed(2)}`),
                    ]),
                    m('button', {
                        class: `w-10 h-10 sm:w-11 sm:h-11 rounded-xl flex items-center justify-center hover:scale-105 active:scale-95 transition-all shadow-sm border ${
                            inCart
                                ? 'bg-green-500 text-white border-green-500 shadow-[0_4px_15px_-3px_rgba(34,197,94,0.4)]'
                                : 'bg-[var(--color-bg-base)] text-[var(--color-primary)] group-hover:bg-[var(--color-primary)] group-hover:text-white border-[var(--color-border)] group-hover:border-[var(--color-primary)] group-hover:shadow-[0_4px_15px_-3px_var(--color-primary-glow)]'
                        }`,
                        onclick: (e: Event) => {
                            e.stopPropagation();
                            if (inCart) {
                                m.route.set('/cart');
                                return;
                            }
                            if ((p.stock || 0) < 1) {
                                Store.addToast('Out of stock', 'error');
                                return;
                            }
                            Store.state.cart.push({
                                id: Date.now(),
                                quantity: 1,
                                images: p.images,
                                product: { id: p.id, name: p.name, price: String(p.price), stock: p.stock }
                            });
                            Store.addToast('Added to cart!', 'success');
                            m.redraw();
                        },
                    }, m('iconify-icon', { icon: inCart ? 'lucide:check' : 'lucide:shopping-bag', class: 'text-xl animate-scale-in' })),
                ]),
            ]),
        ]);
    }
};
